import React, { useId } from 'react';
import { SparkleGradient } from './SparkleGradient';
import { SPARKLE_BURST_ORIGINS, SPARKLE_PATH, SPARKLE_VIEW_BOX } from './sparklePath';

interface SpinnerProps {
    /**
     * Size classes: "size-text" beside a label, so the sparkle matches the text's height, or a
     * fixed size such as "w-8 h-8" when it stands alone. Colour and animation are fixed.
     */
    className?: string;
    /**
     * Accessible label announced to screen readers. Supply it — already translated — whenever the
     * spinner is the only thing reporting that something is in flight. Leave it out when an ancestor
     * already says so (a Button sets aria-busy), and the spinner is then hidden from the a11y tree
     * instead of announcing an unlabelled status.
     */
    label?: string;
    /** Overrides the default data-testid="spinner". */
    testId?: string;
    /**
     * Adds four grains that burst from the sparkle's diagonals at each quarter turn. Meant for a
     * spinner standing on its own (24px and up); beside a label they would be under a pixel.
     */
    sparks?: boolean;
}

export const Spinner: React.FC<SpinnerProps> = ({ className = '', label, testId = 'spinner', sparks = false }) => {
    const gradientId = useId();

    return (
        <svg
            {...(label ? { role: 'status', 'aria-label': label } : { 'aria-hidden': true })}
            data-testid={testId}
            viewBox={SPARKLE_VIEW_BOX}
            overflow="visible"
            fill={`url(#${gradientId})`}
            className={`inline-block animate-sparkle ${sparks ? 'animate-sparkle-burst' : ''} ${className}`}
        >
            <SparkleGradient id={gradientId} />
            {sparks && SPARKLE_BURST_ORIGINS.map(({ cx, cy }) => <circle key={`${cx},${cy}`} cx={cx} cy={cy} r={0.9} />)}
            <path d={SPARKLE_PATH} />
        </svg>
    );
};
